import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react";
import { apiUrl } from "../lib/apiBase";
import { isPlayStoreApp } from "../lib/appMode";

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      ""
    );
  } catch {
    return "";
  }
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function money(cents) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(cents || 0) / 100
  );
}

function dateTime(value) {
  if (!value) return "Date to be announced";
  return new Date(value).toLocaleString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function countdown(startsAt, now) {
  const remaining = Math.max(0, new Date(startsAt).getTime() - now);
  const seconds = Math.floor(remaining / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return { days, hours, minutes, seconds: secs, complete: remaining === 0 };
}

function statusLabel(status) {
  return String(status || "").replaceAll("_", " ").toUpperCase();
}

export default function DigitalVenueEvent() {
  const { eventSlug } = useParams();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const previewCode = params.get("preview") || "";
  const sessionId = params.get("session_id") || "";
  const checkoutState = params.get("checkout") || "";
  const signedIn = Boolean(getToken());
  const nativeStoreApp = isPlayStoreApp();

  const [event, setEvent] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const loadAccess = useCallback(async () => {
    if (!signedIn) return null;
    try {
      const data = await request(`/digital-venue/events/${encodeURIComponent(eventSlug)}/access`);
      setAccess(data);
      return data;
    } catch (err) {
      if (err.status === 403 && err.data?.error === "ticket_required") {
        setAccess({ ok: false, hasTicket: false, playbackReady: false });
        return null;
      }
      if (err.status !== 409) setError(err.message);
      return null;
    }
  }, [eventSlug, signedIn]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const preview = previewCode ? `?preview=${encodeURIComponent(previewCode)}` : "";
      const data = await request(`/digital-venue/events/${encodeURIComponent(eventSlug)}${preview}`);
      setEvent(data.event);

      if (signedIn && checkoutState === "success" && sessionId) {
        setBusy("confirm");
        const confirmed = await request(
          `/digital-venue/events/${encodeURIComponent(eventSlug)}/confirm-checkout`,
          { method: "POST", body: { sessionId } }
        );
        setNotice(
          confirmed.paid
            ? "Your ticket is confirmed. You are on the guest list."
            : "Stripe is still confirming this payment. This page will update automatically."
        );
        setBusy("");
      } else if (checkoutState === "cancelled") {
        setNotice("Checkout was cancelled. No ticket was charged.");
      }

      await loadAccess();
    } catch (err) {
      setError(err.message || "This event could not be loaded.");
    } finally {
      setBusy("");
      setLoading(false);
    }
  }, [checkoutState, eventSlug, loadAccess, previewCode, sessionId, signedIn]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!signedIn) return undefined;
    const timer = window.setInterval(loadAccess, 60000);
    const refresh = () => {
      if (!document.hidden) loadAccess();
    };
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadAccess, signedIn]);

  async function checkout() {
    if (!accepted) return;
    setBusy("checkout");
    setError("");
    try {
      const data = await request(`/digital-venue/events/${encodeURIComponent(eventSlug)}/checkout`, {
        method: "POST",
        body: { acceptTerms: true, previewCode },
      });
      if (!data?.url) throw new Error("Stripe did not return a checkout page.");
      window.location.href = data.url;
    } catch (err) {
      if (err.data?.error === "ticket_already_owned") {
        setNotice("You already have a ticket for this event.");
        await loadAccess();
      } else {
        setError(err.message);
      }
      setBusy("");
    }
  }

  if (loading) {
    return <main className="dv-event"><section className="dv-card">Preparing the digital venue…</section></main>;
  }

  if (!event) {
    return (
      <main className="dv-event">
        <section className="dv-card"><h1>Event unavailable</h1><p>{error || "This event is not available."}</p></section>
      </main>
    );
  }

  const timer = countdown(event.startsAt, now);
  const hasTicket = Boolean(access?.hasTicket);
  const salesOpen = Boolean(event.ticketSalesOpen && ["private_test", "on_sale"].includes(event.status));

  return (
    <main className="dv-event">
      <style>{`
        .dv-event{max-width:1280px;margin:0 auto;padding:18px 16px 84px;color:#fff}.dv-hero,.dv-card{border:1px solid rgba(255,65,77,.3);background:linear-gradient(145deg,rgba(54,6,12,.94),rgba(7,7,9,.97));box-shadow:0 26px 80px rgba(0,0,0,.45)}.dv-hero{border-radius:28px;overflow:hidden;display:grid;grid-template-columns:minmax(0,1.25fr) minmax(300px,.75fr)}.dv-hero-copy{padding:clamp(24px,5vw,58px)}.dv-art{min-height:360px;background:radial-gradient(circle at 50% 25%,rgba(255,73,84,.38),transparent 45%),linear-gradient(160deg,#4b0810,#09090b 65%);display:grid;place-items:center;position:relative}.dv-art img{width:100%;height:100%;object-fit:cover;position:absolute;inset:0}.dv-mark{position:relative;text-align:center;font-weight:1000;font-size:clamp(2.4rem,7vw,6rem);line-height:.85;letter-spacing:-.07em}.dv-eyebrow{font-size:12px;font-weight:1000;letter-spacing:.18em;color:#ffabb2}.dv-title{font-size:clamp(2.7rem,7vw,6.6rem);line-height:.9;letter-spacing:-.055em;margin:12px 0 16px}.dv-subtitle{font-size:clamp(1.05rem,2.2vw,1.45rem);line-height:1.55;color:rgba(255,255,255,.75);max-width:760px}.dv-meta{display:flex;gap:10px;flex-wrap:wrap;margin-top:20px}.dv-pill{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.06);border-radius:999px;padding:8px 11px;font-size:12px;font-weight:900}.dv-pill--live{background:#c61525;border-color:#ff6773}.dv-grid{display:grid;grid-template-columns:minmax(0,1.4fr) minmax(300px,.6fr);gap:18px;margin-top:18px}.dv-card{border-radius:22px;padding:clamp(20px,3vw,32px)}.dv-card h2{font-size:clamp(1.6rem,3.4vw,2.6rem);margin:0 0 12px}.dv-copy{color:rgba(255,255,255,.72);line-height:1.7}.dv-countdown{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:22px 0}.dv-time{border:1px solid rgba(255,255,255,.11);background:rgba(255,255,255,.045);border-radius:16px;padding:14px 8px;text-align:center}.dv-time strong{display:block;font-size:clamp(1.6rem,4vw,2.6rem)}.dv-time span{font-size:10px;color:rgba(255,255,255,.5);font-weight:900;letter-spacing:.12em}.dv-price{font-size:2.5rem;font-weight:1000;margin:8px 0}.dv-btn{width:100%;border:1px solid rgba(255,104,113,.65);background:linear-gradient(135deg,#e31b2c,#74040d);color:#fff;border-radius:999px;padding:14px 18px;font:inherit;font-weight:1000;cursor:pointer;text-decoration:none;text-align:center;display:inline-block}.dv-btn:disabled{opacity:.48;cursor:not-allowed}.dv-check{display:flex;align-items:flex-start;gap:10px;margin:16px 0;color:rgba(255,255,255,.74);font-size:13px;line-height:1.5}.dv-check input{margin-top:3px}.dv-alert{border-radius:14px;padding:13px 15px;margin:0 0 14px}.dv-alert--ok{background:rgba(13,104,54,.34);border:1px solid rgba(79,224,139,.32);color:#c4f9d5}.dv-alert--error{background:rgba(133,13,22,.4);border:1px solid rgba(255,77,90,.38);color:#ffc0c5}.dv-player{aspect-ratio:16/9;border-radius:20px;overflow:hidden;background:#000}.dv-player mux-player{width:100%;height:100%}.dv-ticket{border:1px solid rgba(79,224,139,.28);background:rgba(9,91,45,.22)}.dv-fine{font-size:12px;color:rgba(255,255,255,.48);line-height:1.55;margin-top:14px}@media(max-width:860px){.dv-hero,.dv-grid{grid-template-columns:1fr}.dv-art{min-height:240px}.dv-grid>aside{order:-1}}@media(max-width:460px){.dv-countdown{grid-template-columns:repeat(2,1fr)}}
      `}</style>

      {notice ? <div className="dv-alert dv-alert--ok">{notice}</div> : null}
      {error ? <div className="dv-alert dv-alert--error">{error}</div> : null}

      <section className="dv-hero">
        <div className="dv-hero-copy">
          <div className="dv-eyebrow">EXCLUSIVE GAStreaming LIVE EVENT</div>
          <h1 className="dv-title">{event.title}</h1>
          {event.subtitle ? <p className="dv-subtitle">{event.subtitle}</p> : null}
          <div className="dv-meta">
            <span className={`dv-pill ${event.status === "live" ? "dv-pill--live" : ""}`}>{statusLabel(event.status)}</span>
            <span className="dv-pill">{dateTime(event.startsAt)}</span>
            <span className="dv-pill">Worldwide digital venue</span>
          </div>
        </div>
        <div className="dv-art">
          {event.artworkUrl ? <img src={event.artworkUrl} alt="Event artwork" /> : null}
          <div className="dv-mark">GET<br />ACTIVE<br />LIVE</div>
        </div>
      </section>

      {hasTicket && access?.playbackReady ? (
        <section className="dv-card" style={{ marginTop: 18 }}>
          <div className="dv-eyebrow">TICKET VERIFIED — YOU ARE INSIDE</div>
          <h2>{access.streamType === "live" ? "The show is live." : "Your replay is ready."}</h2>
          <div className="dv-player">
            <MuxPlayer
              playbackId={access.playbackId}
              tokens={{ playback: access.playbackToken }}
              streamType={access.streamType}
              metadata={{
                video_id: event.id,
                video_title: event.title,
                viewer_user_id: access.viewerSessionId,
              }}
              accentColor="#ef233c"
              primaryColor="#ffffff"
              secondaryColor="#09090b"
            />
          </div>
          <p className="dv-fine">Your secure playback access is tied to this account. Recording, restreaming, or sharing access is prohibited.</p>
        </section>
      ) : null}

      <section className="dv-grid">
        <article className="dv-card">
          <div className="dv-eyebrow">THE DIGITAL VENUE</div>
          <h2>{event.performerName ? `One exclusive night with ${event.performerName}` : "One performance. One digital room."}</h2>
          <p className="dv-copy">{event.description || "A ticketed live entertainment experience produced and delivered exclusively through GAStreaming."}</p>
          {!timer.complete ? (
            <div className="dv-countdown">
              {[[timer.days, "DAYS"], [timer.hours, "HOURS"], [timer.minutes, "MINUTES"], [timer.seconds, "SECONDS"]].map(([value, label]) => (
                <div className="dv-time" key={label}><strong>{String(value).padStart(2, "0")}</strong><span>{label}</span></div>
              ))}
            </div>
          ) : null}
          <p className="dv-copy"><strong>Event time:</strong> {dateTime(event.startsAt)}</p>
          {event.allowReplay ? <p className="dv-copy"><strong>Replay:</strong> Available to ticket holders until {dateTime(event.replayEndsAt)}.</p> : <p className="dv-copy"><strong>Live only:</strong> No replay is promised unless the event agreement later authorizes one.</p>}
        </article>

        <aside className={`dv-card ${hasTicket ? "dv-ticket" : ""}`}>
          {hasTicket ? (
            <>
              <div className="dv-eyebrow">YOUR DIGITAL TICKET</div>
              <h2>You're on the guest list.</h2>
              <p className="dv-copy">Come back here when the doors open. This account will receive secure access automatically.</p>
              <span className="dv-pill">ACCESS CONFIRMED</span>
            </>
          ) : (
            <>
              <div className="dv-eyebrow">ONE DIGITAL TICKET</div>
              <div className="dv-price">{money(event.ticketPriceCents)}</div>
              <p className="dv-copy">One ticket provides access for one signed-in GAStreaming account.</p>
              {nativeStoreApp ? (
                <div className="dv-alert dv-alert--ok">Ticket purchases are handled on the GAStreaming website and are not sold inside the mobile store app. Existing ticket holders can watch here after signing in.</div>
              ) : !signedIn ? (
                <Link className="dv-btn" to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}>Sign in to purchase</Link>
              ) : salesOpen && !event.soldOut ? (
                <>
                  <label className="dv-check">
                    <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                    <span>I accept the event-access, anti-sharing, cancellation, and refund terms shown for this private pilot.</span>
                  </label>
                  <button className="dv-btn" disabled={!accepted || busy === "checkout" || busy === "confirm"} onClick={checkout}>
                    {busy === "checkout" ? "Opening secure checkout…" : `Get ticket — ${money(event.ticketPriceCents)}`}
                  </button>
                </>
              ) : (
                <button className="dv-btn" disabled>{event.soldOut ? "Sold out" : "Tickets are not on sale"}</button>
              )}
              <p className="dv-fine">{event.refundPolicy}</p>
            </>
          )}
        </aside>
      </section>
    </main>
  );
}